'use client'

import { useEffect } from 'react'

interface ServiceWorkerRegistrationProps {
  buildId?: string
}

export default function ServiceWorkerRegistration({
  buildId,
}: ServiceWorkerRegistrationProps) {
  useEffect(() => {
    // Only register service worker in production and if supported
    if (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      process.env.NODE_ENV === 'production'
    ) {
      registerServiceWorker(buildId)
    }
  }, [buildId])

  return null // This component doesn't render anything
}

async function registerServiceWorker(buildId?: string) {
  console.log('🚀 ~ registerServiceWorker ~ buildId:', buildId)
  try {
    console.log('[SW Registration] Attempting to register service worker...')

    // Check if service worker is already registered
    const existingRegistration = await navigator.serviceWorker.getRegistration()

    if (existingRegistration) {
      // Check if we need to update due to build ID change
      console.log('[SW Registration] Existing service worker found')

      // Force update check
      await existingRegistration.update()

      // Listen for new service worker
      existingRegistration.addEventListener('updatefound', () => {
        const newWorker = existingRegistration.installing
        if (newWorker) {
          console.log('[SW Registration] New service worker installing...')

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed') {
              if (navigator.serviceWorker.controller) {
                // New service worker available
                console.log('[SW Registration] New service worker ready!')
                showUpdateNotification(newWorker)
              } else {
                // Service worker cached for first time
                console.log(
                  '[SW Registration] Service worker cached for first time'
                )
                showCachedNotification()
              }
            }
          })
        }
      })
    } else {
      // Register new service worker
      console.log('[SW Registration] Registering new service worker...')
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none', // Always check for updates
      })

      console.log(
        '[SW Registration] Service worker registered successfully:',
        registration.scope
      )

      // Handle updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing
        if (newWorker) {
          console.log('[SW Registration] New service worker installing...')

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed') {
              if (navigator.serviceWorker.controller) {
                showUpdateNotification(newWorker)
              } else {
                showCachedNotification()
              }
            }
          })
        }
      })
    }

    // Listen for service worker messages
    navigator.serviceWorker.addEventListener('message', (event) => {
      console.log('[SW Registration] Message from service worker:', event.data)

      if (event.data && event.data.type === 'CACHE_CLEARED') {
        console.log('[SW Registration] Cache cleared by service worker')
      }
    })

    // Handle controller change (new service worker took control)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[SW Registration] Service worker controller changed')
      // Optionally reload the page or show notification
    })
  } catch (error) {
    console.error(
      '[SW Registration] Service worker registration failed:',
      error
    )
  }
}

function showUpdateNotification(newWorker: ServiceWorker) {
  // Create a subtle notification for updates
  const notification = document.createElement('div')
  notification.style.cssText = `
    position: fixed;
    bottom: 20px;
    right: 20px;
    background: #1890ff;
    color: white;
    padding: 16px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 10000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    max-width: 300px;
    cursor: pointer;
    transition: all 0.3s ease;
  `

  notification.innerHTML = `
    <div style="margin-bottom: 8px; font-weight: 500;">
      🚀 App Update Available
    </div>
    <div style="font-size: 12px; opacity: 0.9; margin-bottom: 12px;">
      A new version is ready. Click to update.
    </div>
    <div style="display: flex; gap: 8px;">
      <button style="background: rgba(255,255,255,0.2); border: 1px solid rgba(255,255,255,0.3); color: white; padding: 4px 12px; border-radius: 4px; font-size: 12px; cursor: pointer;" onclick="this.parentElement.parentElement.remove()">
        Later
      </button>
      <button id="update-btn" style="background: white; color: #1890ff; border: none; padding: 4px 12px; border-radius: 4px; font-size: 12px; cursor: pointer; font-weight: 500;">
        Update
      </button>
    </div>
  `

  const updateBtn = notification.querySelector('#update-btn')
  updateBtn?.addEventListener('click', () => {
    console.log('[SW Registration] User requested update')
    newWorker.postMessage({ type: 'SKIP_WAITING' })
    window.location.reload()
  })

  document.body.appendChild(notification)

  // Auto-remove after 30 seconds if not clicked
  setTimeout(() => {
    if (notification.parentElement) {
      notification.remove()
    }
  }, 30000)
}

function showCachedNotification() {
  console.log('[SW Registration] App is now cached for offline use')

  // Optional: Show a less prominent notification for first-time caching
  const notification = document.createElement('div')
  notification.style.cssText = `
    position: fixed;
    bottom: 20px;
    right: 20px;
    background: #52c41a;
    color: white;
    padding: 12px 16px;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    z-index: 10000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.3s ease;
  `

  notification.innerHTML = `
    <div style="display: flex; align-items: center; gap: 8px;">
      <span>✅</span>
      <span>App ready for offline use</span>
    </div>
  `

  notification.addEventListener('click', () => {
    notification.remove()
  })

  document.body.appendChild(notification)

  // Auto-remove after 5 seconds
  setTimeout(() => {
    if (notification.parentElement) {
      notification.remove()
    }
  }, 5000)
}

// Utility function to manually clear cache (for development/debugging)
export function clearServiceWorkerCache() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistration().then((registration) => {
      if (registration) {
        registration.active?.postMessage({ type: 'CACHE_BUST' })
      }
    })
  }
}

// Utility function to check cache status
export async function getServiceWorkerStatus() {
  if (!('serviceWorker' in navigator)) {
    return { supported: false }
  }

  const registration = await navigator.serviceWorker.getRegistration()

  return {
    supported: true,
    registered: !!registration,
    active: !!registration?.active,
    waiting: !!registration?.waiting,
    installing: !!registration?.installing,
    scope: registration?.scope,
  }
}
